<template>
  <div class="flex flex-col h-full w-full bg-white border-r border-gray-200 overflow-y-auto">
    <nav class="flex flex-col py-4">
      <template v-for="(section, sIdx) in menuSections" :key="sIdx">
        <template v-if="section.items.some(canViewMenuItem)">
          <div class="flex flex-col p-3">
            <p class="px-2 font-bold text-gray-400 text-sm">{{ full ? section.label : section.shortLabel }}</p>
            <hr class="text-gray-200"/>
          </div>

          <div class="flex flex-col gap-1">
          <template v-for="(item, idx) in section.items" :key="idx">
            <template v-if="canViewMenuItem(item)">
              <!-- Expanded -->
              <div v-if="full" class="flex flex-col">
                <router-link
                  :to="item.route"
                  class="block mx-2 px-3 py-3 rounded-lg cursor-pointer transition-all duration-200 group"
                  :class="isActive(item.route) ? activeClass : defaultClass">
                  <div class="flex items-center gap-3 text-xs">
                    <i :class="item.icon" class="w-5 text-center transition-transform duration-200 group-hover:scale-110"></i>
                    <span class="font-medium">{{ item.title }}</span>
                  </div>
                </router-link>

                <!-- Submenu -->
                <div
                  v-if="isActive(item.route) && item.children"
                  class="mt-1 mb-2 bg-gray-50 rounded-lg mx-2 py-2">
                  <template v-for="(subItem, idx2) in item.children" :key="idx2">
                    <router-link
                      v-if="canViewMenuItem(subItem)"
                      :to="subItem.route"
                      class="mx-3 px-4 py-2 rounded-md cursor-pointer transition-all duration-200 flex items-center justify-between group"
                      :class="isActive(subItem.route) ? activeSubClass : defaultSubClass">
                      <span class="text-xs font-medium">{{ subItem.title }}</span>
                      <span
                        v-if="subItem.count != null"
                        class="px-2 py-1 rounded-full text-xs font-semibold transition-colors duration-200"
                        :class="isActive(subItem.route) ? 'bg-white text-primary-700' : 'bg-primary text-white'">
                        {{ subItem.count }}
                      </span>
                    </router-link>
                  </template>
                </div>
              </div>

              <!-- Collapsed (icon + tooltip) -->
              <router-link
                v-else
                :to="item.route"
                class="block mx-2 px-3 py-3 rounded-lg cursor-pointer transition-all duration-200 group"
                :class="isActive(item.route) ? activeClass : defaultClass">
                <div class="flex justify-center">
                  <div class="relative inline-block">
                    <i :class="item.icon" class="text-xl transition-transform duration-200 group-hover:scale-110"></i>
                    <div class="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-3 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 pointer-events-none shadow-lg">
                      {{ item.title }}
                      <div class="absolute right-full top-1/2 -translate-y-1/2 -mr-1 border-4 border-transparent border-r-gray-900"></div>
                    </div>
                  </div>
                </div>
              </router-link>
            </template>
          </template>
        </div>
      </template>
    </template>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { useRoute } from "vue-router";
import { useAbility } from "@casl/vue";

// Types
interface MenuItem {
  title: string
  route: string
  icon: string
  action: string
  resource: string
  children?: MenuItem[]
  count?: number
}

interface MenuSection {
  label: string
  shortLabel: string
  items: MenuItem[]
}

interface Props {
  full: boolean
}

defineProps<Props>()

const route = useRoute()

// Shared item styles
const activeClass = "bg-primary-500 text-white font-bold shadow-md"
const defaultClass = "text-gray-700 hover:bg-primary-50 hover:text-primary-700"
const activeSubClass = "bg-primary-200 text-primary-600 shadow-sm"
const defaultSubClass = "text-gray-600 hover:bg-white hover:text-primary-700 hover:shadow-sm"

const menuSections: MenuSection[] = [
  {
    label: "Business",
    shortLabel: "Bs",
    items: [
      {
        title: "Accounts",
        route: "/home/accounts",
        icon: "fa fa-users",
        action: "can_access_system_accounts",
        resource: "general",
        children: [
          {
            title: "Classification",
            route: "/home/accounts/views",
            action: "can_view_system_accounts",
            resource: "general",
            icon: "",
          },
          {
            title: "Permissions",
            route: "/home/accounts/permissions",
            action: "can_view_permission_management",
            resource: "general",
            icon: "",
          }
        ]
      },
      {
        title: "Internal Processing",
        route: "/home/internal-processes",
        icon: "fa fa-copy",
        action: "can_view_internal_processing",
        resource: "general"
      },
      {
        title: "Settings",
        route: "/home/settings",
        icon: "fa fa-gear",
        action: "can_view_mobile_app_settings",
        resource: "general"
      },
      {
        title: "Notifications",
        route: "/home/notifications",
        icon: "fa fa-bell",
        action: "can_view_general_settings",
        resource: "general"
      },
      {
        title: "Default registrar",
        route: "/home/default-registrar",
        icon: "fa fa-user-check",
        action: "can_view_general_settings",
        resource: "general"
      },
      {
        title: "Activity Logs",
        route: "/home/activity-logs",
        icon: "fas fa-list-alt",
        action: "can_view_api_activity_logs",
        resource: "general"
      },
      {
        title: "Monitoring",
        route: "/home/monitoring",
        icon: "fas fa-heartbeat",
        action: "can_view_monitoring",
        resource: "general"
      },
      {
        title: "Data Correction",
        route: "/home/data-correction",
        icon: "fa fa-edit",
        action: "can_perform_data_correction",
        resource: "general",
        children: [
          {
            title: "Start",
            route: "/home/data-correction/start",
            action: "can_perform_data_correction",
            resource: "general",
            icon: "",
          },
          {
            title: "Applications",
            route: "/home/data-correction/applications",
            action: "can_perform_data_correction",
            resource: "general",
            icon: "",
          }
        ]
      },
    ],
  },
  {
    label: "Billing & Revenue",
    shortLabel: "B&R",
    items: [
      {
        title: "Transactions",
        route: "/home/billing/transactions",
        action: "can_view_billing_transactions_settings",
        resource: "general",
        icon: "fas fa-list",
      },
      {
        title: "Payment Reference Numbers",
        route: "/home/billing/prn-manager",
        action: "can_view_billing_prn_settings",
        resource: "general",
        icon: "fas fa-hashtag",
      },
      {
        title: "Service Costs",
        route: "/home/billing/service-costs",
        action: "can_view_billing_service_cost_settings",
        resource: "general",
        icon: "fas fa-money-bill"
      },
      {
        title: "Document Costs",
        route: "/home/billing/document-costs",
        action: "can_view_billing_document_cost_settings",
        resource: "general",
        icon: "fas fa-file-lines"
      },
      {
        title: "Tax Heads",
        route: "/home/billing/tax-heads",
        action: "can_view_billing_tax_heads_settings",
        resource: "general",
        icon: "fas fa-bank"
      },
    ],
  },
  {
    label: "Non Individuals",
    shortLabel: "Ni",
    items: [
      {
        title: "Registration",
        route: "/home/nir/registration",
        action: "can_view_nir_forms",
        resource: "general",
        icon: "fas fa-file-pen",
      },
      {
        title: "Forms",
        route: "/home/nir/forms",
        action: "can_view_nir_forms",
        resource: "general",
        icon: "fas fa-clipboard",
      },
      {
        title: "Settings",
        route: "/home/nir/master-data",
        action: "can_view_nir_forms",
        resource: "general",
        icon: "fas fa-cog",
      },
      {
        title: "Associate Registers",
        route: "/home/nir/associate-registers",
        action: "can_view_nir_institutions",
        resource: "general",
        icon: "fas fa-bank",
      },
    ],
  },
  {
    label: "Registries",
    shortLabel: "Reg",
    items: [
      {
        title: "Registries",
        route: "/home/registry",
        action: "can_access_registry",
        resource: "general",
        icon: "fas fa-database",
      },
    ],
  },
]

const isActive = (target: string): boolean => route.fullPath.includes(target)

const { can } = useAbility()

const canViewMenuItem = (item: MenuItem): boolean => {
  if (!item.action || !item.resource) return true
  return can(item.action, item.resource)
}
</script>
